/**
 * Feature Availability Service
 * 
 * Detects API key availability and determines which features are available
 * based on the presence of required API keys and backend connections.
 */

export interface ApiKeyStatus {
  // Note: We still track backend URLs for informational purposes, but don't restrict features based on them
  reactAppApiBaseUrl: boolean;
  reactAppAtlasBaseUrl: boolean;
  // Proprietary API keys that we actually restrict features on
  openaiApiKey: boolean;
  reactAppOpenaiApiKey: boolean;
  /** Set from GET /api/jira/status — never use REACT_APP_JIRA_* in client code (CRA embeds values in the bundle). */
  jiraBackendConfigured: boolean;
  hereApiKey: boolean;
  graphhopperApiKey: boolean;
  reactAppOpenrouteApiKey: boolean;
}

export interface FeatureAvailability {
  [featureName: string]: {
    available: boolean;
    reason?: string;
    requiredKeys: string[];
  };
}

/**
 * Feature to API key dependency mapping
 *
 * Keys use snake_case to match admin feature-flag names and JWT `flags` claim keys.
 * Features with `[]` are server-gated only (no env key prerequisites).
 *
 * Note: Features that require REACT_APP_API_BASE_URL are restricted because the simulation
 * backend requires Tailscale access (or JWT authentication). Even if the URL is configured,
 * the API calls will fail without proper network access or authentication.
 *
 * REACT_APP_ATLAS_BASE_URL is publicly accessible (sandbox.liftango.com), so features using
 * it are not restricted.
 *
 * Proprietary API keys are also restricted as they require actual API keys.
 */
function usesOpenAiOrLogic(requiredKeys: string[]): boolean {
  return (
    requiredKeys.includes('OPENAI_API_KEY') &&
    requiredKeys.includes('REACT_APP_OPENAI_API_KEY')
  );
}

const FEATURE_DEPENDENCIES: Record<string, string[]> = {
  // Simulation API features (require REACT_APP_API_BASE_URL + Tailscale/JWT)
  simulations: ['REACT_APP_API_BASE_URL'],
  map_save_load: ['REACT_APP_API_BASE_URL'],

  // Routing API features (proprietary API keys)
  graphhopper_routing: ['GRAPHHOPPER_API_KEY'],
  here_routing: ['HERE_API_KEY'],
  openroute_routing: ['REACT_APP_OPENROUTE_API_KEY'],

  // Product / analysis / platform (server-gated unless noted elsewhere)
  simulation_results_analytics: [],
  fixed_routes: [],
  generate_rides: [],
  population_analysis: [],
  spatial_analysis: [],
  rules_engine: [],
  rules_engine_admin: [],
  rules_engine_override: [],
  rules_engine_driver_model: [],
  advance_rostering: [],
  meeting_points_generator: [],
  attribute_table: [],
  data_transfer_drawer: [],
  environment_tokens: [],
  google_basemap: [],
  import_gtfs: [],
  import_osm_locations: [],
  import_trips: [],
  import_kml: [],
  auto_generate_fixed_route_fleet: [],
};

/**
 * Detect the presence of all required API keys
 */
export function detectApiKeys(): ApiKeyStatus {
  return {
    reactAppApiBaseUrl: !!process.env.REACT_APP_API_BASE_URL,
    reactAppAtlasBaseUrl: !!(process.env.REACT_APP_ATLAS_BASE_URL || process.env.REACT_APP_CARTO_BASE_URL),
    openaiApiKey: !!process.env.OPENAI_API_KEY,
    reactAppOpenaiApiKey: !!process.env.REACT_APP_OPENAI_API_KEY,
    jiraBackendConfigured: false,
    hereApiKey: !!process.env.HERE_API_KEY,
    graphhopperApiKey: !!process.env.GRAPHHOPPER_API_KEY,
    reactAppOpenrouteApiKey: !!process.env.REACT_APP_OPENROUTE_API_KEY,
  };
}

/**
 * Get availability status for all features based on API key detection
 */
export function getFeatureAvailability(apiKeyStatus: ApiKeyStatus): FeatureAvailability {
  const availability: FeatureAvailability = {};

  // Check each feature's dependencies
  Object.entries(FEATURE_DEPENDENCIES).forEach(([featureName, requiredKeys]) => {
    // OpenAI-backed features work if EITHER key is present
    if (usesOpenAiOrLogic(requiredKeys)) {
      const hasOpenAI = apiKeyStatus.openaiApiKey || apiKeyStatus.reactAppOpenaiApiKey;
      availability[featureName] = {
        available: hasOpenAI,
        requiredKeys: requiredKeys,
        reason: !hasOpenAI
          ? 'OpenAI API key is required for AI features. Configure OPENAI_API_KEY or REACT_APP_OPENAI_API_KEY.'
          : undefined
      };
    } else {
      // For other features, ALL keys must be present (AND logic)
      const missingKeys: string[] = [];
      
      requiredKeys.forEach(key => {
        // Map environment variable names to ApiKeyStatus properties
        const statusKey = mapEnvVarToStatusKey(key);
        if (!apiKeyStatus[statusKey]) {
          missingKeys.push(key);
        }
      });

      availability[featureName] = {
        available: missingKeys.length === 0,
        requiredKeys: requiredKeys,
        reason: missingKeys.length > 0 
          ? `Missing required API keys: ${missingKeys.join(', ')}`
          : undefined
      };
    }
  });

  availability.jira_integration = {
    available: apiKeyStatus.jiraBackendConfigured,
    requiredKeys: ['jira_integration'],
    reason: apiKeyStatus.jiraBackendConfigured
      ? undefined
      : getApiKeyMessage('jira_integration'),
  };

  return availability;
}

/**
 * Map environment variable name to ApiKeyStatus property key
 */
function mapEnvVarToStatusKey(envVar: string): keyof ApiKeyStatus {
  const mapping: Record<string, keyof ApiKeyStatus> = {
    'REACT_APP_API_BASE_URL': 'reactAppApiBaseUrl',
    'REACT_APP_ATLAS_BASE_URL': 'reactAppAtlasBaseUrl',
    'OPENAI_API_KEY': 'openaiApiKey',
    'REACT_APP_OPENAI_API_KEY': 'reactAppOpenaiApiKey',
    'HERE_API_KEY': 'hereApiKey',
    'GRAPHHOPPER_API_KEY': 'graphhopperApiKey',
    'REACT_APP_OPENROUTE_API_KEY': 'reactAppOpenrouteApiKey',
  };
  
  return mapping[envVar] || 'reactAppApiBaseUrl'; // fallback
}

/**
 * Get user-friendly message for missing API key or backend URL
 */
export function getApiKeyMessage(envVarName: string): string {
  const messages: Record<string, string> = {
    // Backend URLs (require Tailscale/JWT)
    'REACT_APP_API_BASE_URL': 'Simulation backend connection is required. Connect to Tailscale or configure REACT_APP_API_BASE_URL to enable simulation features.',
    
    // Proprietary API keys
    'OPENAI_API_KEY': 'OpenAI API key is required for AI features. Configure OPENAI_API_KEY or REACT_APP_OPENAI_API_KEY.',
    'REACT_APP_OPENAI_API_KEY': 'OpenAI API key is required for AI features. Configure REACT_APP_OPENAI_API_KEY or OPENAI_API_KEY.',
    jira_integration:
      'Jira integration requires the Atlas backend to have a Jira API token configured (server-side env or AWS Secrets Manager). Do not set Jira tokens in REACT_APP_* variables used by the web build.',
    'HERE_API_KEY': 'HERE API key is required for HERE bus routing. Configure HERE_API_KEY. OSRM routing is available as an alternative.',
    'GRAPHHOPPER_API_KEY': 'GraphHopper API key is required for GraphHopper bus routing. Configure GRAPHHOPPER_API_KEY. OSRM routing is available as an alternative.',
    'REACT_APP_OPENROUTE_API_KEY': 'OpenRouteService API key is required. Configure REACT_APP_OPENROUTE_API_KEY.',
  };
  
  return messages[envVarName] || `API key ${envVarName} is required for this feature.`;
}

